/**
 * commentary-grid-selection.service.ts — DLITE-3849 Task 11
 *
 * Backs the commentary-ui drawer's `pickEntities` ("Pick from grid") callback.
 * The drawer asks Helios "what is selected right now?" and Helios answers with
 * the UNION of the current selection across ALL open commentary-enabled grids,
 * mapped to refs.
 *
 * Each commentary-enabled grid registers a getSelectionAsEntityRefs() callback
 * keyed by its panel key (the grid owns the selection→ref mapping because it
 * knows its own id fields and row shape). A grid is "open" exactly while it has
 * a registered getter — it registers on init and unregisters on ngOnDestroy.
 * `collectSelection` aggregates across every currently-registered getter rather
 * than reading a single focused grid, so the drawer works regardless of which
 * grid opened it and without depending on fragile focus tracking.
 *
 * Accounts are typically filtered out downstream by the drawer (the thread's
 * own account is uninteresting as a pick target), so a grid returning account
 * refs alongside others is expected and harmless here.
 *
 * This ships the "read the current selection" behaviour. A dedicated
 * enter-selection-mode UX (highlight rows, confirm) is an explicit fast-follow
 * and is intentionally NOT implemented here.
 */
import { Injectable } from '@angular/core'
import { CommentaryEntityRef, CommentaryEntityType } from '../types/thread-jump'

/** A grid's current selection mapped to entity refs. */
export type GridSelectionGetter = () => CommentaryEntityRef[]

@Injectable({ providedIn: 'root' })
export class CommentaryGridSelectionService {
  private readonly getters = new Map<string, GridSelectionGetter>()

  /**
   * Register (or replace) a grid's selection getter under its panel key.
   * Re-using the same key overwrites — safe across grid re-init.
   */
  register(panelKey: string, getter: GridSelectionGetter): void {
    this.getters.set(panelKey, getter)
  }

  /** Drop a grid's getter — called from the grid's ngOnDestroy. */
  unregister(panelKey: string): void {
    this.getters.delete(panelKey)
  }

  /**
   * The union of the current selection across all open commentary-enabled
   * grids, as refs, optionally constrained to a single entity `type`.
   *
   * Each registered getter is invoked, its results concatenated, de-duped by
   * `${type}:${id}`, and (if a `type` is given) filtered to that type. A getter
   * that throws is skipped so one misbehaving grid can't break the rest.
   * Returns [] when no grids are registered or nothing is selected.
   */
  collectSelection(type?: CommentaryEntityType): CommentaryEntityRef[] {
    const seen = new Set<string>()
    const result: CommentaryEntityRef[] = []

    for (const getter of this.getters.values()) {
      let refs: CommentaryEntityRef[]
      try {
        refs = getter() ?? []
      } catch {
        // Skip a grid whose getter throws — the rest still contribute.
        continue
      }

      for (const ref of refs) {
        if (type != null && ref.type !== type) continue

        const key = `${ref.type}:${ref.id}`
        if (seen.has(key)) continue
        seen.add(key)

        // Carry the optional friendly label (DLITE-3911) so a picked row's chip
        // can show it; the id stays the stable jump/lookup key.
        result.push({ type: ref.type, id: ref.id, label: ref.label })
      }
    }

    return result
  }
}
